import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField
} from '@mui/material';
import { useUser } from '../../../../contexts/UserContext';
import { useWallet } from '@solana/wallet-adapter-react';
import { useSecureNetwork } from '../../../../utils/secureNetwork';
import { useTwoFactorPrompt } from '../../../../components/TwoFactorPrompt/useTwoFactorPrompt';
import { useNetwork } from '../../../../utils/network';
import { useShowSnackbar } from '../../../../utils/snackbar';
import { holdMessage, holdUntil, formatHoldUntil } from '../../../../utils/securityHold';
import { track, trackError } from '../../../../utils/track';
import { useWalletReady } from '../../../../utils/useWalletReady';
import { Transaction } from '@solana/web3.js';
import { convertToLamports } from '../../../../constants/functions';
import { signCanonicalTransaction } from './signCanonicalTransaction';

// Utility function to format idempotency key as user-friendly transaction ID
const formatTransactionId = (idempotencyKey: string): string => {
  try {
    // Split the idempotency key: prefix_userId_timestamp_uuid
    const parts = idempotencyKey.split('_');
    if (parts.length >= 4) {
      // Extract the UUID (last part) and timestamp
      const uuid = parts[parts.length - 1];
      const timestamp = parts[parts.length - 2];

      // Use first 8 characters of UUID and last 6 digits of timestamp
      const uuidPart = uuid.substring(0, 8).toUpperCase();
      const timestampPart = timestamp.slice(-6);

      return `TX-${uuidPart}-${timestampPart}`;
    }

    // Fallback: use a hash of the full key
    const hash = idempotencyKey.split('').reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);

    return `TX-${Math.abs(hash).toString(36).toUpperCase().substring(0, 12)}`;
  } catch (error) {
    // Ultimate fallback
    return `TX-${idempotencyKey.substring(0, 8).toUpperCase()}...`;
  }
};

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

// Two equal-height arena panels (My Balance | Earned Tokens), centred content.
const panelSx = {
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  p: { xs: 2.5, md: 3.5 },
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.22)',
  background:
    'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
  boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: '0 auto auto 0',
    width: 320,
    height: 180,
    pointerEvents: 'none',
    background:
      'radial-gradient(420px 220px at 0 0, rgba(56, 232, 255, 0.08), transparent 65%)'
  }
} as const;

const kickSx = {
  font: `800 10px ${MONO}`,
  letterSpacing: '0.28em',
  textTransform: 'uppercase',
  color: '#8ea3b3'
} as const;

const bigNumSx = {
  mt: 1.5,
  fontFamily: MONO,
  fontWeight: 900,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
} as const;

// Arena outlined input (dark fill, cyan focus, mono text) — replaces the old
// MUI "filled" underline look; focus/error states preserved.
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(7, 11, 17, 0.55)',
    color: '#e8f4fb',
    fontFamily: MONO,
    '& fieldset': { borderColor: 'rgba(56, 232, 255, 0.28)' },
    '&:hover fieldset': { borderColor: 'rgba(56, 232, 255, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#38e8ff' },
    '&.Mui-error fieldset': { borderColor: '#ff6b6b' }
  },
  '& .MuiInputLabel-root': { color: '#8ea3b3' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#38e8ff' },
  '& .MuiInputLabel-root.Mui-error': { color: '#ff6b6b' },
  '& .MuiFormHelperText-root': {
    color: '#8ea3b3',
    mx: 0,
    mt: 1,
    fontFamily: MONO,
    fontSize: '0.72rem',
    lineHeight: 1.5
  },
  '& .MuiFormHelperText-root.Mui-error': { color: '#ff6b6b' },
  '& input': { color: '#e8f4fb' },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    { WebkitAppearance: 'none', margin: 0 }
} as const;

export const SecureTokenBalance = () => {
  const { user, setUser } = useUser();
  const { promptForTwoFactor, twoFactorModal } = useTwoFactorPrompt();

  const { publicKey, signTransaction, signMessage, connected } = useWallet();
  // Connect-on-demand: turns a dropped/locked wallet into an actionable reconnect
  // prompt instead of a dead-end "not connected" error (the recurring report).
  const { ensureReady, notReadyMessage } = useWalletReady();
  const {
    secureTokenWithdraw,
    secureTokenWithdrawConfirm,
    secureTokenDeposit,
    secureTokenDepositConfirm,
    checkSecureServicesHealth,
    securePut,
    secureGet
  } = useSecureNetwork();
  const { post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();

  const [amount, setAmount] = useState('');
  const [sendingTransaction, setSendingTransaction] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [isDepositing, setIsDepositing] = useState(false);
  const [servicesHealthy, setServicesHealthy] = useState<boolean | null>(null);
  const [currentIdempotencyKey, setCurrentIdempotencyKey] = useState<
    string | null
  >(null);
  const [withdrawalLimits, setWithdrawalLimits] = useState<any>(null);
  const [convertAmount, setConvertAmount] = useState('');
  const [isConverting, setIsConverting] = useState(false);

  const MAX_MONTHLY_INC = 10000;

  const handleMaxConvert = () => {
    const earned = Number(user?.earnedTokens || 0);
    const max = Math.min(earned, MAX_MONTHLY_INC);
    setConvertAmount(String(max));
  };

  const earnedBalance = Number(user?.earnedTokens || 0);
  const isNumericInput = (value: string) => /^\d+(\.\d+)?$/.test(value.trim());
  const convertTrimmed = convertAmount.trim();
  const convertParsed = convertTrimmed === '' ? NaN : Number(convertTrimmed);

  const convertValidationError =
    convertTrimmed === ''
      ? 'Enter an amount to convert'
      : !isNumericInput(convertTrimmed) || !Number.isFinite(convertParsed)
        ? 'Enter a valid number'
        : convertParsed <= 0
          ? 'Amount must be greater than 0'
          : convertParsed > MAX_MONTHLY_INC
            ? `Maximum is ${MAX_MONTHLY_INC.toLocaleString()} INC per month`
            : convertParsed > earnedBalance
              ? `Insufficient earned tokens. You have ${earnedBalance.toLocaleString()} INC`
              : null;

  const canConvert =
    !isConverting &&
    connected &&
    !!publicKey &&
    !!signTransaction &&
    user?.userHasHotWallet &&
    !convertValidationError;

  // Check services health and fetch withdrawal limits on component mount
  React.useEffect(() => {
    const fetchLimits = async () => {
      try {
        // Only fetch if we don't have limits yet or if they're stale
        const lastFetch = localStorage.getItem('withdrawal_limits_last_fetch');
        const now = Date.now();

        if (lastFetch && now - parseInt(lastFetch) < 60000) {
          // Use cached limits if fetched within last minute
          const cachedLimits = localStorage.getItem('withdrawal_limits_cache');
          if (cachedLimits) {
            setWithdrawalLimits(JSON.parse(cachedLimits));
            return;
          }
        }

        const limits = await secureGet(
          '/secure-tokens/withdrawal-limits',
          true
        );
        setWithdrawalLimits(limits);

        // Cache the results
        localStorage.setItem('withdrawal_limits_cache', JSON.stringify(limits));
        localStorage.setItem('withdrawal_limits_last_fetch', now.toString());
      } catch (error: any) {
        console.error('Failed to fetch withdrawal limits:', error);

        // Fallback to basic limits display if API fails
        if (
          error.message?.includes('Too many requests') ||
          error.message?.includes('Rate limit')
        ) {
          // Use fallback limits to avoid blocking the UI
          setWithdrawalLimits({
            isNewAccount: true, // Assume new account as safer default
            maxDaily: null,
            newAccountCumulativeLimit: 200000,
            remainingNewAccountLimit: 200000,
            daysUntilFullLimits: 90
          });
        }
      }
    };

    fetchLimits();
  }, [checkSecureServicesHealth, secureGet, user?.id]);

  const handleSecureTokenOperation = async (type: 'deposit' | 'withdraw') => {
    if (!ensureReady()) {
      // Re-prompts the wallet (or opens the picker) when the adapter silently
      // dropped/locked the connection; the user then taps again.
      trackError('wallet.token', 'wallet_not_ready', { op: type });
      showSnackbar(notReadyMessage, 'warning');
      return;
    }
    // Redundant after ensureReady() — kept so TS narrows publicKey/signTransaction.
    if (!connected || !publicKey || (!signTransaction && !signMessage)) {
      return;
    }

    if (servicesHealthy === false) {
      showSnackbar(
        'Token services are currently unavailable. Please try again later.',
        'error'
      );
      return;
    }

    const amountInLamports = convertToLamports(amount);

    // Security: Removed sensitive data logging

    // Enhanced pre-transaction validation
    if (!amount || amount.trim() === '') {
      showSnackbar('📝 Please enter an amount to proceed', 'error');
      return;
    }

    if (!amountInLamports || isNaN(Number(amount))) {
      showSnackbar('🔢 Please enter a valid numeric amount', 'error');
      return;
    }

    // Enhanced validation for minimum amount
    const minAmount = BigInt('100000'); // 1 token minimum (100000 = 1 * 10^5)
    if (BigInt(amountInLamports) < minAmount) {
      showSnackbar(
        '📏 Minimum amount is 1 token. Please enter at least 1.',
        'error'
      );
      return;
    }

    // Maximum amount validation for safety
    const maxAmount = BigInt('10000000000'); // 100K tokens maximum
    if (BigInt(amountInLamports) > maxAmount) {
      showSnackbar(
        '📊 Maximum amount is 100,000 tokens. Please enter a smaller amount.',
        'error'
      );
      return;
    }

    // Balance validation for withdrawals
    if (type === 'withdraw') {
      const userBalance = user?.tokenAmount ? parseFloat(user.tokenAmount) : 0;
      const requestedAmount = parseFloat(amount);

      if (requestedAmount > userBalance) {
        showSnackbar(
          `💰 Insufficient balance. You have ${userBalance} INC but tried to withdraw ${requestedAmount} INC.`,
          'error'
        );
        return;
      }

      // Note: New account withdrawal limits are enforced server-side
      // Server will return appropriate error messages for accounts under 90 days old

      // Warn if withdrawing more than 80% of balance
      if (requestedAmount > userBalance * 0.8) {
        const remaining = (userBalance - requestedAmount).toFixed(2);
        showSnackbar(
          `⚠️ Large withdrawal: You'll have ${remaining} INC remaining. Proceed carefully.`,
          'warning'
        );
      }
    }

    // 48 hours after a 2FA reset nothing leaves: say so before asking for a code
    if (type === 'withdraw') {
      const held = holdUntil(user);
      if (held) {
        showSnackbar(`Withdrawals are paused until ${formatHoldUntil(held)} after your 2FA reset.`, 'warning');
        return;
      }
    }

    // Blockchain operations require a 2FA code (verified server-side).
    const twoFactorToken = await promptForTwoFactor();
    if (!twoFactorToken) return;

    setSendingTransaction(true);
    type === 'deposit' ? setIsDepositing(true) : setIsWithdrawing(true);

    try {
      // First, ensure the wallet address is up to date in the database
      const currentWalletAddress = publicKey.toString();
      // Security: Removed wallet address logging

      // Update wallet address if needed (using secure network)
      if (user?.walletAddress !== currentWalletAddress) {
        // Wallet address mismatch detected, updating securely...
        try {
          const result = await securePut(
            '/user/wallet-address',
            { walletAddress: currentWalletAddress },
            'WALLET_ADDRESS_UPDATE',
            true
          );

          // Wallet update successful

          if (result.updated) {
            setUser({
              ...user,
              walletAddress: result.walletAddress || currentWalletAddress
            });
            // User context updated with wallet
          }
        } catch (walletUpdateError: any) {
          // Handle wallet update error silently
          showSnackbar(
            walletUpdateError.message ||
              'Failed to update wallet address securely',
            'error'
          );
          return;
        }
      }

      // Step 1: Create the secure transaction with idempotency key
      // Creating secure transaction

      let createResponse;
      if (type === 'withdraw') {
        createResponse = await secureTokenWithdraw(
          amountInLamports,
          twoFactorToken
        );
      } else {
        createResponse = await secureTokenDeposit(
          amountInLamports,
          twoFactorToken
        );
      }

      // Transaction created successfully

      const { transaction, idempotencyKey, expectedMessageB64 } =
        createResponse;
      setCurrentIdempotencyKey(idempotencyKey);

      if (!transaction || !idempotencyKey) {
        throw new Error('Invalid response from secure transaction service');
      }

      // Transaction received with idempotency key

      // Step 2: Deserialize and validate transaction
      const transactionBuffer = Buffer.from(transaction, 'base64');
      const deserializedTx = Transaction.from(transactionBuffer);

      // Transaction deserialized successfully
      // Pre-signing transaction validation passed

      // Enhanced security validation
      if (deserializedTx.feePayer?.toString() !== currentWalletAddress) {
        throw new Error(
          'Transaction was created for a different wallet. Please try again.'
        );
      }

      // Step 3: Sign transaction
      // Starting transaction signing...
      const signedTx = await signCanonicalTransaction({
        transaction: deserializedTx,
        publicKey,
        signTransaction,
        signMessage,
        expectedMessageB64
      });
      // Transaction signed successfully

      // Post-signing validation passed

      const signedTxBase64 = signedTx
        .serialize({
          requireAllSignatures: false,
          verifySignatures: false
        })
        .toString('base64');

      // Confirming transaction with blockchain...

      // Step 4: Confirm transaction with blockchain-first approach
      let confirmResponse;
      if (type === 'withdraw') {
        confirmResponse = await secureTokenWithdrawConfirm(
          signedTxBase64,
          idempotencyKey
        );
      } else {
        confirmResponse = await secureTokenDepositConfirm(
          signedTxBase64,
          idempotencyKey
        );
      }

      // Transaction confirmed successfully

      // Step 5: Update user state
      setUser({
        ...user,
        tokenAmount: confirmResponse.tokenAmount,
        walletAddress: currentWalletAddress
      });

      showSnackbar(
        `🎉 ${confirmResponse.message} Your balance has been updated successfully!`,
        'success'
      );
      // CRM: a real INC token transfer confirmed (deposit/withdraw). See utils/track.ts.
      track('token.' + type, { amount: Number(amount) || undefined });

      // Refresh withdrawal limits after successful transaction
      try {
        const limits = await secureGet(
          '/secure-tokens/withdrawal-limits',
          true
        );
        setWithdrawalLimits(limits);
        localStorage.setItem('withdrawal_limits_cache', JSON.stringify(limits));
        localStorage.setItem(
          'withdrawal_limits_last_fetch',
          Date.now().toString()
        );
      } catch (error) {
        console.warn(
          'Failed to refresh withdrawal limits after transaction:',
          error
        );
      }
    } catch (error: any) {
      trackError('wallet.token', error, { op: type });
      const held = holdMessage(error);
      if (held) {
        showSnackbar(held, 'warning');
        return;
      }
      console.error(`🚨 Token ${type} error details:`, {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        type: type,
        amount: amount,
        userBalance: user?.tokenAmount
      });

      // Enhanced error handling with detailed user guidance
      if (error.message?.includes('Rate limit exceeded')) {
        showSnackbar(
          '⏱️ Too many requests. Please wait 1 minute before trying again to prevent spam.',
          'warning'
        );
      } else if (
        error.message?.includes(
          'Daily withdrawal limit exceeded for established accounts'
        )
      ) {
        // Extract established account daily limit information
        const establishedLimitMatch = error.message.match(
          /You have withdrawn ([\d.]+) INC today out of your ([\d.]+) INC daily limit.*Remaining: ([\d.]+) INC/
        );
        if (establishedLimitMatch) {
          const [, withdrawn, dailyLimit, remaining] = establishedLimitMatch;
          showSnackbar(
            `📊 Daily limit reached! You've withdrawn ${withdrawn} INC today out of your ${dailyLimit} INC daily limit. Remaining: ${remaining} INC. Limit resets at midnight.`,
            'warning'
          );
        } else {
          showSnackbar(
            '📊 Daily withdrawal limit reached for established accounts (100,000 INC/day). Limit resets at midnight.',
            'warning'
          );
        }
      } else if (error.message?.includes('Daily limit exceeded')) {
        showSnackbar(
          '📊 Daily withdrawal limit reached. You can withdraw more tomorrow or contact support for higher limits.',
          'warning'
        );
      } else if (
        error.message?.includes('New account 90-day withdrawal limit exceeded')
      ) {
        // Extract cumulative limit information if available
        const cumulativeLimitMatch = error.message.match(
          /You have withdrawn ([\d.]+) INC out of your ([\d.]+) INC total limit.*You can only withdraw ([\d.]+) INC more.*Full limits unlock in (\d+) days/
        );
        if (cumulativeLimitMatch) {
          const [, withdrawn, totalLimit, remaining, daysRemaining] =
            cumulativeLimitMatch;
          showSnackbar(
            `🆕 90-day withdrawal limit reached! You've used ${withdrawn} INC of your ${totalLimit} INC total limit for new accounts. You can withdraw ${remaining} INC more. Full limits unlock in ${daysRemaining} days.`,
            'warning'
          );
        } else {
          showSnackbar(
            '🆕 New account 90-day withdrawal limit exceeded. Accounts under 90 days old have a cumulative 200,000 INC withdrawal limit.',
            'warning'
          );
        }
      } else if (
        error.message?.includes('temporarily disabled for maintenance')
      ) {
        showSnackbar(
          `🔧 ${type.charAt(0).toUpperCase() + type.slice(1)} operations are temporarily under maintenance. Please try again later or contact support.`,
          'warning'
        );
      } else if (error.message?.includes('Transaction replay detected')) {
        showSnackbar(
          '✅ This transaction was already processed successfully. Check your balance - it may have already updated.',
          'info'
        );
      } else if (
        error.message?.includes('Insufficient balance') ||
        error.message?.includes('insufficient amount') ||
        error.message?.includes('User has insufficient') ||
        error.message?.includes('does not have enough')
      ) {
        // Extract balance information if available
        const balanceMatch = error.message.match(
          /Available: ([\d.]+) INC, Requested: ([\d.]+) INC/
        );
        if (balanceMatch) {
          const [, available, requested] = balanceMatch;
          showSnackbar(
            `💰 Insufficient balance! You have ${available} INC but tried to ${type} ${requested} INC. Please enter a smaller amount.`,
            'error'
          );
        } else {
          showSnackbar(
            `💰 Insufficient balance for this ${type}. Please check your balance and try a smaller amount.`,
            'error'
          );
        }
      } else if (
        error.message?.includes('User rejected') ||
        error.message?.includes('User denied')
      ) {
        showSnackbar(
          '❌ Transaction cancelled. You can try again anytime.',
          'info'
        );
      } else if (
        error.message?.includes('Network') ||
        error.message?.includes('timeout')
      ) {
        showSnackbar(
          '🌐 Network connection error. Please check your internet connection and try again in a few moments.',
          'error'
        );
      } else if (error.message?.includes('Invalid wallet address')) {
        showSnackbar(
          '🔗 Wallet connection issue. Please disconnect and reconnect your wallet, then try again.',
          'error'
        );
      } else if (error.message?.includes('Transaction failed to confirm')) {
        showSnackbar(
          '⏳ Transaction timed out on the blockchain. It may still process - please check your balance in a few minutes before retrying.',
          'warning'
        );
      } else if (
        error.message?.includes('Insufficient funds for transaction')
      ) {
        showSnackbar(
          '💸 Not enough SOL to pay for transaction fees. Please add some SOL to your wallet to cover network fees.',
          'error'
        );
      } else if (error.message?.includes('Invalid transaction')) {
        showSnackbar(
          '⚠️ Transaction validation failed. Please refresh the page and try again with a new transaction.',
          'error'
        );
      } else if (error.message?.includes('Wallet not connected')) {
        showSnackbar(
          '🔌 Wallet not connected. Please connect your wallet and try again.',
          'error'
        );
      } else if (error.message?.includes('MAINTENANCE_MODE')) {
        showSnackbar(
          `🔧 ${type.charAt(0).toUpperCase() + type.slice(1)} operations are currently under maintenance. Please try again later.`,
          'warning'
        );
      } else if (error.message?.includes('ADMIN_REQUIRED')) {
        showSnackbar(
          `🔒 ${type.charAt(0).toUpperCase() + type.slice(1)} operations are currently restricted. Please contact support if you need assistance.`,
          'warning'
        );
      } else {
        // Enhanced generic error handling with better parsing
        let errorMsg = error.message || `${type} failed`;

        // Check if it's a wrapped error message
        if (
          errorMsg.includes('TOKEN_WITHDRAWAL failed') ||
          errorMsg.includes('TOKEN_DEPOSIT failed')
        ) {
          // Try to extract the original error from server logs or provide helpful guidance
          if (type === 'withdraw') {
            errorMsg = `Withdrawal failed. This could be due to insufficient balance, network issues, or maintenance. Please check your balance and try again.`;
          } else {
            errorMsg = `Deposit failed. This could be due to network issues, wallet problems, or maintenance. Please try again.`;
          }
        }

        // Check for common error patterns in wrapped messages
        if (
          errorMsg.toLowerCase().includes('insufficient') ||
          errorMsg.toLowerCase().includes('balance')
        ) {
          showSnackbar(
            `💰 Balance issue detected. Please check your balance and try a smaller amount for ${type}.`,
            'error'
          );
        } else if (
          errorMsg.toLowerCase().includes('wallet') ||
          errorMsg.toLowerCase().includes('connect')
        ) {
          showSnackbar(
            `🔗 Wallet connection issue. Please disconnect and reconnect your wallet, then try again.`,
            'error'
          );
        } else if (
          errorMsg.toLowerCase().includes('network') ||
          errorMsg.toLowerCase().includes('connection')
        ) {
          showSnackbar(
            `🌐 Network error occurred. Please check your internet connection and try again in a few moments.`,
            'error'
          );
        } else {
          showSnackbar(
            `❌ ${errorMsg}. If this persists, try refreshing the page or contact support with these details: "${errorMsg.substring(0, 100)}"`,
            'error'
          );
        }
      }
    } finally {
      setSendingTransaction(false);
      setIsDepositing(false);
      setIsWithdrawing(false);
      setCurrentIdempotencyKey(null);
    }
  };

  const handleConvertEarnedTokens = async () => {
    if (!ensureReady()) {
      showSnackbar(notReadyMessage, 'warning');
      return;
    }
    // Redundant after ensureReady() — kept so TS narrows publicKey/signTransaction.
    if (!connected || !publicKey || !signTransaction) {
      return;
    }

    if (convertValidationError) {
      showSnackbar(convertValidationError, 'error');
      return;
    }

    setIsConverting(true);

    try {
      const intent = await post(
        '/earned-token-conversions/create-intent',
        {
          amountINC: convertAmount,
          connectedWallet: publicKey.toString()
        },
        true
      );

      const txBuffer = Buffer.from(intent.transactionBase64, 'base64');
      const tx = Transaction.from(txBuffer);
      const signed = await signTransaction(tx);

      const signedBase64 = signed
        .serialize({
          requireAllSignatures: false,
          verifySignatures: false
        })
        .toString('base64');

      const result = await post(
        '/earned-token-conversions/confirm',
        {
          conversionId: intent.conversionId,
          signedTransactionBase64: signedBase64,
          connectedWallet: publicKey.toString()
        },
        true
      );

      setUser({
        ...user,
        tokenAmount: result.balances.tokenAmount,
        earnedTokens: result.balances.earnedTokens
      });

      showSnackbar('✅ Tokens moved to your internal wallet!', 'success');
      // CRM: an earned→internal INC conversion confirmed. See utils/track.ts.
      track('token.convert', { amount: Number(convertAmount) || undefined });
      setConvertAmount('');
    } catch (error: any) {
      showSnackbar(error.message || 'Conversion failed', 'error');
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <Box>
      {twoFactorModal}
      <Box
        sx={{
          display: { xs: 'block', md: 'grid' },
          gridTemplateColumns: { md: '1fr 1fr' },
          gap: 3
        }}
      >
        {/* ===== My Balance (left half) ===== */}
        <Box sx={{ ...panelSx, mb: { xs: 3, md: 0 } }}>
          <Typography sx={kickSx}>My Balance · INC</Typography>
          <Typography
            sx={{
              ...bigNumSx,
              color: '#38e8ff',
              textShadow: '0 0 26px rgba(56,232,255,0.28)'
            }}
          >
            {user?.tokenAmount}
          </Typography>

          <TextField
          label="Enter Amount"
          variant="outlined"
          fullWidth
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ ...inputSx, mt: 2.5 }}
          disabled={sendingTransaction || servicesHealthy === false}
          helperText={
            servicesHealthy === false
              ? 'Services unavailable'
              : (() => {
                  const maxPerTransaction =
                    withdrawalLimits?.maxPerTransaction || 100000;
                  const baseText = `Available: ${user?.tokenAmount || '0'} INC • Min: 1 token • Max: ${maxPerTransaction.toLocaleString()} tokens`;

                  if (!withdrawalLimits) {
                    return `${baseText} • Loading limits...`;
                  }

                  if (withdrawalLimits.isWithdrawalLimitExempt) {
                    return `${baseText} • Daily withdrawal limit exception active`;
                  }

                  if (withdrawalLimits.isNewAccount) {
                    const remaining =
                      withdrawalLimits.remainingNewAccountLimit || 0;
                    const total =
                      withdrawalLimits.newAccountCumulativeLimit || 50000;
                    return `${baseText} • New account: ${remaining.toLocaleString()} INC remaining of ${total.toLocaleString()} INC total (${withdrawalLimits.daysUntilFullLimits}d until 100k daily limits)`;
                  } else {
                    const remaining =
                      withdrawalLimits.remainingDailyLimit || 100000;
                    const daily = withdrawalLimits.maxDaily || 100000;
                    return `${baseText} • Daily limit: ${remaining.toLocaleString()} INC remaining of ${daily.toLocaleString()} INC (resets at midnight)`;
                  }
                })()
          }
          error={Boolean(
            amount && (isNaN(Number(amount)) || Number(amount) <= 0)
          )}
          type="number"
          inputProps={{
            min: '1',
            max: '100000',
            step: '1'
          }}
        />

        {/* Current Operation Status */}
        {currentIdempotencyKey && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" color="primary">
              🔒 Secure Transaction ID:{' '}
              {formatTransactionId(currentIdempotencyKey)}
            </Typography>
          </Box>
        )}

        {user.userHasHotWallet ? (
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              width: '100%'
            }}
          >
            <Button
              variant="contained"
              onClick={() => handleSecureTokenOperation('deposit')}
              data-track="token.deposit_click"
              disabled={sendingTransaction || servicesHealthy === false}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#43a047' }
              }}
            >
              {isDepositing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Deposit'
              )}
            </Button>

            <Button
              variant="contained"
              onClick={() => handleSecureTokenOperation('withdraw')}
              data-track="token.withdraw_click"
              disabled={sendingTransaction || servicesHealthy === false}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                backgroundColor: '#f44336',
                '&:hover': { backgroundColor: '#d32f2f' }
              }}
            >
              {isWithdrawing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Withdraw'
              )}
            </Button>

            <Button
              variant="contained"
              onClick={() =>
                window.open(
                  'https://raydium.io/swap/?inputMint=sol&outputMint=Fe5c469ADZyvnZrB8gdDsYt5eLKodaVYhYJCdunVr1nA',
                  '_blank'
                )
              }
              data-track="token.buy_raydium"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Buy Tokens
            </Button>
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              If you want to Deposit/Withdraw tokens, and see this message,
              please contact support via Discord.
            </Typography>
          </Box>
        )}
        </Box>

        {/* ===== Earned Tokens (right half) ===== */}
        <Box sx={panelSx}>
          <Typography sx={kickSx}>Earned Tokens</Typography>
          <Typography
            sx={{
              ...bigNumSx,
              color: '#ffcf40',
              textShadow: '0 0 26px rgba(255,207,64,0.25)'
            }}
          >
            {user?.earnedTokens}
          </Typography>

        {user.userHasHotWallet && (
          <Box sx={{ mt: 3, px: 2, width: '100%' }}>
            <TextField
              label="Amount to convert"
              variant="outlined"
              fullWidth
              value={convertAmount}
              onChange={(e) => setConvertAmount(e.target.value)}
              type="text"
              inputProps={{
                inputMode: 'decimal',
                placeholder: '0'
              }}
              sx={{ ...inputSx, mb: 2 }}
              error={Boolean(convertTrimmed) && Boolean(convertValidationError)}
              helperText={
                convertTrimmed
                  ? convertValidationError ||
                    `Available: ${earnedBalance.toLocaleString()} INC`
                  : `Available: ${earnedBalance.toLocaleString()} INC`
              }
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2
              }}
            >
              <Button
                variant="outlined"
                onClick={handleMaxConvert}
                data-track="token.convert_max"
                disabled={isConverting}
                fullWidth
              >
                Max (10k or balance)
              </Button>

              <Button
                variant="contained"
                onClick={handleConvertEarnedTokens}
                data-track="token.convert_click"
                disabled={!canConvert}
                fullWidth
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': { backgroundColor: '#1565c0' }
                }}
              >
                {isConverting ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  'Convert to Internal Wallet'
                )}
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Maximum 10,000 INC per month. Only 1 conversion allowed per month.
              You pay the small SOL network fee.
            </Typography>
          </Box>
        )}

        {/* <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant="caption" textAlign="center">
            Minimum Amount needed to move/withdraw: 500INC
          </Typography>
        </Box> */}

        {/* <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-evenly' }}>
          <Button
            variant="contained"
            // onClick={() => handleSecureTokenOperation('deposit')}
            // disabled={sendingTransaction || servicesHealthy === false}
            disabled={true}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#43a047' }
            }}
          >
            {isDepositing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Move all to Member Portal'
            )}
          </Button>

          <Button
            variant="contained"
            // onClick={() => handleSecureTokenOperation('withdraw')}
            // disabled={sendingTransaction || servicesHealthy === false}
            disabled={true}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              backgroundColor: '#f44336',
              '&:hover': { backgroundColor: '#d32f2f' }
            }}
          >
            {isWithdrawing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Withdraw all to wallet'
            )}
          </Button>
        </Box> */}
        </Box>
      </Box>
    </Box>
  );
};
